import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, InputGroup } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import apiClient from '../api/axios';

const EventPlanItemModal = ({ show, handleClose, onSave, event, item, responsibleSuggestions, modalType }) => {
    const determineModalType = () => {
        if (item) {
            if (item.is_group_header) return 'header';
            if (item.song_id) return 'song';
            return 'item';
        }
        return modalType;
    };

    const effectiveModalType = determineModalType();

    const getInitialFormData = () => ({
        name: item?.name || '',
        description: item?.description || '',
        duration: item?.duration || 0,
        comment: item?.comment || '',
        responsible: item?.responsible || '',
        song_id: item?.song_id || null,
        arrangement_id: item?.arrangement_id || null,
        is_group_header: item?.is_group_header || (effectiveModalType === 'header'),
    });

    const [formData, setFormData] = useState(getInitialFormData);
    const [time, setTime] = useState({ minutes: 0, seconds: 0 });
    const [selectedSong, setSelectedSong] = useState(null);
    const [arrangements, setArrangements] = useState([]);

    useEffect(() => {
        if (show) {
            const initialData = getInitialFormData();
            setFormData(initialData);

            const totalSeconds = initialData.duration || 0;
            setTime({ 
                minutes: Math.floor(totalSeconds / 60),
                seconds: totalSeconds % 60 
            });

            if (initialData.song_id && item?.song) {
                const songOption = { value: item.song.id, label: item.song.title, song: item.song };
                setSelectedSong(songOption);
                setArrangements(item.song.arrangements || []);
            } else {
                setSelectedSong(null);
                setArrangements([]);
            }
        } else {
            setFormData(getInitialFormData());
            setTime({ minutes: 0, seconds: 0 });
            setSelectedSong(null);
            setArrangements([]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item, show, modalType]);

    useEffect(() => {
        if (formData.arrangement_id) {
            const fetchAverageDuration = async () => {
                try {
                    const response = await apiClient.get(`/arrangements/${formData.arrangement_id}/average_mp3_duration`);
                    const durationInSeconds = response.data.average_duration;
                    if (durationInSeconds > 0) {
                        setTime({
                            minutes: Math.floor(durationInSeconds / 60),
                            seconds: Math.round(durationInSeconds % 60)
                        });
                    }
                } catch (error) {
                    console.error("Could not fetch average arrangement duration:", error);
                }
            };

            // Only fetch duration if we are in 'song' mode to avoid unnecessary calls
            if (effectiveModalType === 'song') {
                fetchAverageDuration();
            }
        }
    }, [formData.arrangement_id, effectiveModalType]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleTimeChange = (e) => {
        const { name, value } = e.target;
        setTime(prev => ({ ...prev, [name]: parseInt(value, 10) || 0 }));
    };

    const handleSongChange = (option) => {
        setSelectedSong(option);
        const defaultArrangement = option?.song?.arrangements?.find(arr => arr.is_default);
        setFormData(prev => ({ 
            ...prev, 
            song_id: option?.value || null, 
            arrangement_id: defaultArrangement?.id || null 
        }));
        setArrangements(option?.song?.arrangements || []);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const totalDuration = (time.minutes * 60) + time.seconds;
        let dataToSave = { ...formData, duration: totalDuration };

        if (effectiveModalType === 'header') {
            dataToSave = { name: dataToSave.name, is_group_header: true, duration: 0 };
        } else if (effectiveModalType === 'song') {
            dataToSave = { 
                duration: totalDuration, 
                song_id: dataToSave.song_id, 
                arrangement_id: dataToSave.arrangement_id, 
                description: dataToSave.description,
                responsible: dataToSave.responsible, 
                comment: dataToSave.comment, 
                is_group_header: false 
            };
        } else { // 'item'
            dataToSave = { name: dataToSave.name, description: dataToSave.description, duration: totalDuration, responsible: dataToSave.responsible, comment: dataToSave.comment, is_group_header: false };
        }
        
        onSave(dataToSave);
    };

    const loadSongOptions = (inputValue, callback) => {
        if (!inputValue) return callback([]);
        apiClient.get(`/songs/search?q=${inputValue}`)
            .then(response => {
                const options = response.data.map(song => ({ value: song.id, label: song.title, song }));
                callback(options);
            });
    };

    const modalTitles = {
        header: item ? 'Редактировать заголовок' : 'Добавить заголовок',
        item: item ? 'Редактировать пункт' : 'Добавить пункт',
        song: item ? 'Редактировать песню' : 'Добавить песню',
    };

    const renderFields = () => {
        const responsibleField = (
            <Form.Group className="mb-3">
                <Form.Label>Ответственный</Form.Label>
                <Form.Control list="responsible-suggestions" type="text" name="responsible" value={formData.responsible} onChange={handleChange} />
            </Form.Group>
        );

        const commentField = (
            <Form.Group className="mb-3">
                <Form.Label>Комментарий</Form.Label>
                <Form.Control as="textarea" rows={2} name="comment" value={formData.comment} onChange={handleChange} />
            </Form.Group>
        );

        const durationField = (
            <Form.Group className="mb-3">
                <Form.Label>Длительность</Form.Label>
                <Row>
                    <Col><InputGroup><Form.Control type="number" name="minutes" value={time.minutes} onChange={handleTimeChange} min="0" /><InputGroup.Text>мин.</InputGroup.Text></InputGroup></Col>
                    <Col><InputGroup><Form.Control type="number" name="seconds" value={time.seconds} onChange={handleTimeChange} min="0" max="59" /><InputGroup.Text>сек.</InputGroup.Text></InputGroup></Col>
                </Row>
            </Form.Group>
        );

        switch (effectiveModalType) {
            case 'header':
                return (
                    <Form.Group className="mb-3">
                        <Form.Label>Название заголовка</Form.Label>
                        <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} required />
                    </Form.Group>
                );
            case 'item':
                return (
                    <>
                        {durationField}
                        <Form.Group className="mb-3">
                            <Form.Label>Название пункта</Form.Label>
                            <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Описание</Form.Label>
                            <Form.Control type="text" name="description" value={formData.description} onChange={handleChange} />
                        </Form.Group>
                        {responsibleField}
                        {commentField}
                    </>
                );
            case 'song':
                return (
                    <>
                        {durationField}
                        <Form.Group className="mb-3">
                            <Form.Label>Песня</Form.Label>
                            <AsyncSelect cacheOptions loadOptions={loadSongOptions} onChange={handleSongChange} value={selectedSong} placeholder="Начните вводить название..." isClearable required />
                        </Form.Group>
                        {selectedSong && (
                            <Form.Group className="mb-3">
                                <Form.Label>Аранжировка</Form.Label>
                                <Form.Select name="arrangement_id" value={formData.arrangement_id} onChange={handleChange} required>
                                    {arrangements.map(arr => (
                                        <option key={arr.id} value={arr.id}>{arr.name} ({arr.key || '-'})</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>Описание</Form.Label>
                            <Form.Control type="text" name="description" value={formData.description} onChange={handleChange} />
                        </Form.Group>
                        {responsibleField}
                        {commentField}
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>{modalTitles[effectiveModalType]}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    {renderFields()}
                    <datalist id="responsible-suggestions">
                        {responsibleSuggestions && responsibleSuggestions.map((suggestion, index) => (
                            <option key={index} value={suggestion} />
                        ))}
                    </datalist>
                    <div className="d-flex justify-content-end mt-4">
                        <Button variant="secondary" onClick={handleClose} className="me-2">Отмена</Button>
                        <Button variant="primary" type="submit">Сохранить</Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default EventPlanItemModal;
